import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import api from '../api';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const now = new Date();

export default function Salaries() {
  const [records, setRecords] = useState([]);
  const [staff, setStaff] = useState([]);
  const [filterMonth, setFilterMonth] = useState(now.getMonth() + 1);
  const [filterYear, setFilterYear] = useState(now.getFullYear());
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ staff: '', month: now.getMonth() + 1, year: now.getFullYear(), amount: '', status: 'Unpaid', notes: '' });
  const [msg, setMsg] = useState('');

  const load = async () => {
    const [salRes, staffRes] = await Promise.all([
      api.get('/staff/salary', { params: { month: filterMonth, year: filterYear } }),
      api.get('/staff')
    ]);
    setRecords(salRes.data);
    setStaff(staffRes.data);
  };

  useEffect(() => { load(); }, [filterMonth, filterYear]);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await api.post('/staff/salary', form);
      setShowModal(false);
      setMsg('Salary record saved!');
      load();
    } catch (err) { setMsg(err.response?.data?.message || 'Error'); }
  };

  const togglePaid = async (rec) => {
    const newStatus = rec.status === 'Paid' ? 'Unpaid' : 'Paid';
    await api.put(`/staff/salary/${rec._id}`, { status: newStatus, paidDate: newStatus === 'Paid' ? new Date() : null });
    load();
  };

  const totalPaid = records.filter(r => r.status === 'Paid').reduce((s, r) => s + r.amount, 0);
  const totalUnpaid = records.filter(r => r.status === 'Unpaid').reduce((s, r) => s + r.amount, 0);

  return (
    <Layout title="Staff Salaries">
      {msg && <div className="alert alert-info">{msg} <button style={{ float:'right', background:'none', border:'none', cursor:'pointer' }} onClick={() => setMsg('')}>✕</button></div>}

      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3,1fr)', maxWidth: 500 }}>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#38a169', color: '#fff' }}>💵</div>
          <div className="stat-info"><h3 style={{ fontSize: 16 }}>PKR {totalPaid.toLocaleString()}</h3><p>Paid</p></div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#e53e3e', color: '#fff' }}>⏳</div>
          <div className="stat-info"><h3 style={{ fontSize: 16 }}>PKR {totalUnpaid.toLocaleString()}</h3><p>Pending</p></div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#3182ce', color: '#fff' }}>👥</div>
          <div className="stat-info"><h3>{records.length}</h3><p>Records</p></div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">💵 Salary Records — {MONTHS[filterMonth - 1]} {filterYear}</h3>
          <div className="flex gap-10">
            <select value={filterMonth} onChange={e => setFilterMonth(e.target.value)}>
              {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
            <select value={filterYear} onChange={e => setFilterYear(e.target.value)}>
              {[2024, 2025, 2026, 2027].map(y => <option key={y}>{y}</option>)}
            </select>
            <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>➕ Add Record</button>
          </div>
        </div>

        <div className="table-wrapper">
          <table>
            <thead>
              <tr><th>Staff</th><th>Role</th><th>Amount</th><th>Status</th><th>Notes</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {records.length === 0 ? (
                <tr><td colSpan={6} className="text-center" style={{ padding: 30, color: '#718096' }}>No salary records for this month</td></tr>
              ) : records.map(r => (
                <tr key={r._id}>
                  <td><strong>{r.staff?.name}</strong></td>
                  <td><span className={`badge ${r.staff?.role === 'Security Guard' ? 'badge-warning' : 'badge-info'}`}>{r.staff?.role}</span></td>
                  <td>PKR {r.amount.toLocaleString()}</td>
                  <td><span className={`badge ${r.status === 'Paid' ? 'badge-success' : 'badge-danger'}`}>{r.status}</span></td>
                  <td style={{ fontSize: 12 }}>{r.notes || '-'}</td>
                  <td>
                    <button className={`btn btn-sm ${r.status === 'Paid' ? 'btn-warning' : 'btn-success'}`} onClick={() => togglePaid(r)}>
                      {r.status === 'Paid' ? '↩ Unpaid' : '✅ Mark Paid'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Salary Record</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSave}>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Staff Member *</label>
                    <select value={form.staff} onChange={e => {
                      const s = staff.find(st => st._id === e.target.value);
                      setForm(f => ({ ...f, staff: e.target.value, amount: s?.salary || '' }));
                    }} required>
                      <option value="">-- Select Staff --</option>
                      {staff.filter(s => s.status === 'Active').map(s => <option key={s._id} value={s._id}>{s.name} ({s.role})</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Month</label>
                    <select value={form.month} onChange={e => setForm(f => ({ ...f, month: e.target.value }))}>
                      {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Year</label>
                    <select value={form.year} onChange={e => setForm(f => ({ ...f, year: e.target.value }))}>
                      {[2024, 2025, 2026, 2027].map(y => <option key={y}>{y}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Amount (PKR) *</label>
                    <input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} required min="0" />
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                      <option>Unpaid</option>
                      <option>Paid</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Notes</label>
                    <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
                  </div>
                </div>
                <div className="form-actions">
                  <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary">💾 Save</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
